import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  padded?: boolean;
}

/**
 * Surface container shared by every page section. Optional header slot pairs
 * a title/subtitle on the left with action controls on the right.
 */
export function Card({
  title,
  subtitle,
  actions,
  padded = true,
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <section
      className={cn(
        'rounded-2xl border border-gray-200 bg-white shadow-card',
        'dark:border-white/10 dark:bg-gray-900 dark:shadow-card-dark',
        className,
      )}
      {...rest}
    >
      {(title !== undefined || actions !== undefined) && (
        <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4 dark:border-white/10">
          <div className="min-w-0">
            {title !== undefined && (
              <h2 className="truncate text-base font-semibold text-gray-900 dark:text-white">
                {title}
              </h2>
            )}
            {subtitle !== undefined && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {subtitle}
              </p>
            )}
          </div>
          {actions && <div className="flex shrink-0 gap-2">{actions}</div>}
        </header>
      )}
      <div className={cn(padded && 'p-5')}>{children}</div>
    </section>
  );
}
